import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from '@resy/ui-kitchen';
import {
  setScrolledActiveSection,
  getRetinaImage,
  setHashValueWithOffset,
  scrollTo,
} from '../../AmexMarketingUtils/AmexUtils';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexSecurityCenterKeepAccountSafe.scss';

const AmexSecurityCenterKeepAccountSafe = (
  {
    awsFolderName,
    baseRequestUrl,
    keepAccountSafeConfig: {
      id: sectionId,
      title = '',
      subtitle = '',
      intro = '',
      icon,
      image: {
        desktop: desktopImg = {},
        mobile: mobileImg = {},
      } = {},
      action_items: actionItems = '',
      action_items_copy: actionItemsCopy = '',
    } = {},
    matchMedia,
    dispatch,
    hash,
    updateHash,
  }
) => {
  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const sectionIcon = `${urlPrefix}${icon}`;
  const imgName = getRetinaImage(desktopImg, mobileImg, matchMedia.lg);
  const image = `${urlPrefix}${imgName}`;
  const [linkClicked, setLinkClicked] = useState(null);

  const sanitizedTitle = sanitizeString(title);
  const sanitizedIntro = sanitizeString(intro);
  const sanitizedSubtitle = sanitizeString(subtitle);
  const sanitizedActionItemsCopy = sanitizeString(actionItemsCopy);

  const renderActionItems = actionItems.map((actionItem) => {
    const {
      copy_1: copyPart1 = '',
      copy_2: copyPart2 = '',
      link = {},
    } = actionItem;
    const {
      url,
      aria_label: linkAria,
    } = link;
    if (!copyPart1) return false;

    const sanitizedCopyPart1 = sanitizeString(copyPart1);
    const sanitizedCopyPart2 = sanitizeString(copyPart2);

    return (
      <li
        key={copyPart1}
        className="AmexSecurityCenterKeepAccountSafe__item"
      >
        {copyPart1 && (
          <span
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopyPart1,
            }}
          />
        )}
        {Object.keys(link).length > 0 && (
          <Link
            href={window?.location?.href?.split('#')[0] + url}
            aria-label={linkAria}
            className="AmexSecurityCenterKeepAccountSafe__activityList--link"
            onClick={() => {
              updateHash(url);
              scrollTo(url);
              setLinkClicked(url);
            }}
          >
            {link.label}
          </Link>
        )}
        {copyPart2 && (
          <span
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopyPart2,
            }}
          />
        )}
      </li>
    );
  });
  const isMobile = !matchMedia.md;
  setHashValueWithOffset(isMobile, 190, 210, sectionId, hash, linkClicked);

  const sections = useRef([]);

  useEffect(() => {
    sections.current = document.querySelectorAll('[data-section]');
    window.addEventListener('scroll', () => { setLinkClicked(null); setScrolledActiveSection(sections, dispatch); });

    return () => {
      window.removeEventListener('scroll', () => { setScrolledActiveSection(sections, dispatch); });
    };
  }, []);

  return (
    <section
      data-section
      id={`${sectionId}_section`}
      className="AmexSecurityCenterKeepAccountSafe"
    >
      <div className="container">
        {sectionIcon && (
          <img
            className="AmexSecurityCenterKeepAccountSafe__icon"
            src={sectionIcon}
            alt=""
          />
        )}
        {title && (
          <h2
            id={sectionId}
            className="AmexSecurityCenterKeepAccountSafe__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedTitle,
            }}
          />
        )}
        {intro && (
          <p
            className="AmexSecurityCenterKeepAccountSafe__intro"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedIntro,
            }}
          />
        )}
        <div className="AmexSecurityCenterKeepAccountSafe__wrapper">
          {matchMedia.lg && (
            <div
              className="AmexSecurityCenterKeepAccountSafe__wrapper--img"
              style={{
                backgroundImage: `url(${image})`,
              }}
            />
          )}
          <div className="AmexSecurityCenterKeepAccountSafe__wrapper--content">
            {subtitle && (
              <h3
                className="AmexSecurityCenterKeepAccountSafe__subtitle"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedSubtitle,
                }}
              />
            )}
            {(imgName && !matchMedia.lg) && (
              <img className="AmexSecurityCenterKeepAccountSafe__img--mobile" src={image} alt="" />
            )}
            {actionItems && (
              <ol className="AmexSecurityCenterKeepAccountSafe__items">
                {renderActionItems}
              </ol>
            )}
            {actionItemsCopy && (
              <p
                className="AmexSecurityCenterKeepAccountSafe__action-copy"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedActionItemsCopy,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

AmexSecurityCenterKeepAccountSafe.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  keepAccountSafeConfig: PropTypes.shape({
    image: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }).isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    section_id: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    intro: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    action_items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    action_items_copy: PropTypes.string.isRequired,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  dispatch: PropTypes.string.isRequired,
  updateHash: PropTypes.func.isRequired,
  hash: PropTypes.string.isRequired,
};

export { AmexSecurityCenterKeepAccountSafe as ComponentUnderTest };
export default connect()(AmexSecurityCenterKeepAccountSafe);
